import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import {
    email,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';

import styles from './assets/districts-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistDistrict } from 'src/store/actions/district-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { DropZoneField } from 'src/components/DropZoneField';
import { FieldInputMask } from 'src/components/redux-form/fields/FieldInputMask';
import { FieldTextArea } from 'src/components/redux-form/fields/FieldTextArea';

export interface DistrictFormData {
    id: string | number;
    name: string;
    address: string;
    contact: string;
    email: string;
    phone: string;
    info: string;
}

interface DistrictFormProps {
    files: any;
    setFiles: (it: []) => void;
    removeFile: (it: any) => void;
}

type InjectedProps = InjectedFormProps<DistrictFormData, DistrictFormProps>;

const validators: Validators<DistrictFormData> = {
    name: [required({ message: 'Name is required.' }), maxLength({ max: 255 })],
    address: [required({ message: 'Address is required.' }), maxLength({ max: 255 })],
    contact: [required({ message: 'Contact is required.' }), maxLength({ max: 255 })],
    phone: [required({ message: 'Phone is required.' }), minPhoneLength({ min: 14 })],
    info: [maxLength({ max: 255 })],
    email: [required({ message: 'Email is required.' }), email(), minLength({ min: 1 }), maxLength({ max: 255 })],
};

const DistrictForm: React.FunctionComponent<InjectedProps & DistrictFormProps> = ({
    handleSubmit,
    error,
    dirty,
    files,
    setFiles,
    removeFile,
    submitting,
    initialValues,
}: InjectedProps & DistrictFormProps) => {
    const isNew = !initialValues || !initialValues.id;

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <FieldInput
                name="id"
                type="hidden"
                wrapperConfig={{
                    classes: {
                        inputWrapper: 'm-0',
                    },
                }}
            />
            <FieldInput
                name="name"
                type="text"
                validate={validators.name}
                wrapperConfig={{
                    label: 'Name *',
                }}
            />
            <FieldInput
                name="address"
                type="text"
                validate={validators.address}
                wrapperConfig={{
                    label: 'Address: *',
                }}
            />
            <FieldInput
                name="contact"
                type="text"
                validate={validators.contact}
                wrapperConfig={{
                    label: 'Contact Person: *',
                }}
            />
            <FieldInput
                name="email"
                type="text"
                validate={validators.email}
                wrapperConfig={{
                    label: 'Email: *',
                }}
            />
            <FieldInputMask
                mask={'+1 999-999-9999'}
                name="phone"
                type="phone"
                validate={validators.phone}
                wrapperConfig={{
                    label: 'Phone Number: *',
                }}
            />
            <FieldTextArea
                name="info"
                validate={validators.info}
                wrapperConfig={{
                    label: 'Additional Info:',
                }}
            />

            <DropZoneField files={files} setFiles={setFiles} removeFile={(id: string | number) => removeFile(id)} />
            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.DISTRICTS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<DistrictFormData, DistrictFormProps>({
    form: 'district-form',
    enableReinitialize: true,
    onSubmit: async (data: DistrictFormData, dispatch: Dispatch<any>, props: DistrictFormProps) => {
        await asyncPersistDistrict(data, props.files, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<DistrictFormData, DistrictFormProps>,
    ) {
        const isNew = !props.initialValues || !props.initialValues.id;

        toast.success(`District was successfully ${isNew ? 'created' : 'updated'}.`);
    },
    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(DistrictForm);
